import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCustomerOrders,
  selectCustomerOrders,
  selectOrderLoading,
  selectOrderError,
} from "../../../slice/order/orderSlice";
import { DATE_PRESETS, dateInRange } from "../../../utils/dateRange";

const avatarColor = (name) => {
  const colors = ["var(--milliyo-forest)", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899"];
  if (!name) return colors[0];
  return colors[name.charCodeAt(0) % colors.length];
};
const BASE_URL = process.env.REACT_APP_API_URL;

const statusStyles = {
  pending:      { bg: "#fff7ed", text: "#f59e0b", border: "#fde68a" },
  accepted:     { bg: "#eff6ff", text: "#3b82f6", border: "#bfdbfe" },
  preparing:    { bg: "#f5f3ff", text: "#8b5cf6", border: "#ddd6fe" },
  ready:        { bg: "#ecfeff", text: "#06b6d4", border: "#a5f3fc" },
  out_delivery: { bg: "#eef2ff", text: "var(--milliyo-forest)", border: "#c7d2fe" },
  out_pickup:   { bg: "#eef2ff", text: "var(--milliyo-forest)", border: "#c7d2fe" },
  delivered:    { bg: "#ecfdf5", text: "#10b981", border: "#a7f3d0" },
  shipped:      { bg: "#ecfdf5", text: "#10b981", border: "#a7f3d0" },
  cancelled:    { bg: "#fef2f2", text: "#ef4444", border: "#fecaca" },
};

const formatStatus = (s) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata",
    day: "2-digit", month: "short", year: "numeric",
  });
};

const CustomerOrders = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const orders  = useSelector(selectCustomerOrders);
  const loading = useSelector(selectOrderLoading);
  const error   = useSelector(selectOrderError);

  const [customer, setCustomer] = useState(state?.customer || null);
  const [dateFilter, setDateFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchCustomerOrders(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (orders?.[0]?.user && !customer) {
      setCustomer(orders[0].user);
    }
  }, [orders, customer]);

  const totalSpent = orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);

  // Client-side IST date-range filter over the loaded orders. Header
  // stat tiles stay lifetime totals; the list below reflects the range.
  const visibleOrders = orders.filter((o) => dateInRange(o.createdAt, dateFilter));

  return (
    <div className="container-fluid" style={{padding:"10px"}}>
      <div style={{ minHeight: "calc(100vh - 72px)" }}>

        {/* ── Header card ── */}
        <div style={{
          background: "var(--mly-surface)", borderRadius: 16, padding: "22px 26px",
          marginBottom: 20, boxShadow: "var(--mly-shadow-sm)",
          display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              width: 38, height: 38, borderRadius: 10, border: "1.5px solid var(--mly-border)",
              background: "var(--mly-surface-2)", color: "var(--mly-text-soft)",
              cursor: "pointer", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M14.6 5.4a1.2 1.2 0 0 1 0 1.7L9.7 12l4.9 4.9a1.2 1.2 0 0 1-1.7 1.7l-5.7-5.7a1.2 1.2 0 0 1 0-1.7l5.7-5.7a1.2 1.2 0 0 1 1.7 0Z" /></svg>
          </button>

          <div style={{
            width: 48, height: 48, borderRadius: "50%",
            background: avatarColor(customer?.name),
            color: "#fff", fontWeight: 700, fontSize: 18,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            {customer?.name ? customer.name.charAt(0).toUpperCase() : "?"}
          </div>

          <div style={{ flex: 1, minWidth: 180 }}>
            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 19 }}>
              {customer?.name || "Customer"}'s Orders
            </h4>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--mly-text-muted)" }}>
              {customer?.phoneNumber || "-"}{customer?.email ? ` · ${customer.email}` : ""}
            </p>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{
              background: "#eff6ff", borderRadius: 12, padding: "10px 20px",
              textAlign: "center", border: "1px solid #c7d2fe",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--milliyo-forest)", letterSpacing: 0.5 }}>TOTAL ORDERS</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#1e1b4b" }}>{orders.length}</div>
            </div>
            <div style={{
              background: "#ecfdf5", borderRadius: 12, padding: "10px 20px",
              textAlign: "center", border: "1px solid #a7f3d0",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#10b981", letterSpacing: 0.5 }}>TOTAL SPENT</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#064e3b" }}>₹{totalSpent}</div>
            </div>
          </div>
        </div>

        {/* ── Date-range Filter Chips (client-side, IST) ── */}
        <div style={{
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8,
          marginBottom: 16,
        }}>
          {DATE_PRESETS.map((d) => {
            const isActive = dateFilter === d.key;
            return (
              <button
                key={d.key}
                onClick={() => setDateFilter(d.key)}
                style={{
                  padding: "6px 14px",
                  fontSize: 12, fontWeight: isActive ? 700 : 600,
                  cursor: "pointer",
                  borderRadius: 20,
                  border: `1.5px solid ${isActive ? "var(--milliyo-forest)" : "var(--mly-border)"}`,
                  background: isActive ? "var(--milliyo-forest)" : "var(--mly-surface-3)",
                  color: isActive ? "#fff" : "var(--mly-text)",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s",
                }}
              >
                {d.label}
              </button>
            );
          })}
        </div>

        {/* ── Body ── */}
        {loading ? (
          <div style={{ background: "var(--mly-surface)", borderRadius: 16, padding: "60px 0", textAlign: "center" }}>
            <div className="spinner-border text-primary" role="status" style={{ width: 36, height: 36 }} />
            <div style={{ marginTop: 12, color: "var(--mly-text-muted)", fontSize: 13 }}>Loading orders...</div>
          </div>
        ) : error ? (
          <div style={{ background: "var(--mly-surface)", borderRadius: 16, padding: "40px 0", textAlign: "center", color: "#ef4444" }}>
            {error}
          </div>
        ) : visibleOrders.length === 0 ? (
          <div style={{
            background: "var(--mly-surface)", borderRadius: 16, padding: "60px 0",
            textAlign: "center", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 10,
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%", background: "var(--mly-surface-3)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293a1 1 0 0 0 .707 1.707H17m-10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
              </svg>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>No orders found</span>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
              {dateFilter === "all"
                ? "This customer hasn't placed any orders yet"
                : "No orders in the selected date range"}
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {visibleOrders.map((order) => {
              const sStyle = statusStyles[order.status] || statusStyles.pending;
              const firstItem = order.items?.[0];
              const extraCount = (order.items?.length || 0) - 1;

              return (
                <div
                  key={order._id}
                  onClick={() => navigate(`/admin/customer/details/${order._id}`)}
                  style={{
                    background: "var(--mly-surface)", borderRadius: 16, padding: "18px 22px",
                    boxShadow: "var(--mly-shadow-sm)", cursor: "pointer",
                    border: "1px solid transparent", transition: "all 0.15s",
                    display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#c7d2fe";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(14,58,44,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "transparent";
                    e.currentTarget.style.boxShadow = "var(--mly-shadow-sm)";
                  }}
                >
                  {/* Product thumbnail */}
                  <div style={{
                    width: 54, height: 54, borderRadius: 12, background: "var(--mly-surface-3)",
                    flexShrink: 0, overflow: "hidden", display: "flex",
                    alignItems: "center", justifyContent: "center",
                  }}>
                    {firstItem?.images?.[0] ? (
              <img src={`${BASE_URL}/uploads/${firstItem.images[0]}`} 
                     
                      alt={firstItem.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    )}
                  </div>

                  {/* Order id + items */}
                  <div style={{ minWidth: 180, flex: "1 1 200px" }}>
                    <div style={{ fontWeight: 700, fontSize: 14, fontFamily: "monospace", color: "var(--mly-text)" }}>
                      {order.orderId}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--mly-text-muted)", marginTop: 3 }}>
                      {firstItem?.name || "Item"}{extraCount > 0 && ` +${extraCount} more`}
                    </div>
                  </div>

                  {/* Date */}
                  <div style={{ minWidth: 110, fontSize: 13, color: "var(--mly-text-soft)" }}>
                    {formatDate(order.createdAt)}
                  </div>

                  {/* Payment */}
                  <div style={{ minWidth: 130, fontSize: 13, color: "var(--mly-text-soft)", textTransform: "capitalize" }}>
                    {order.payment?.method || "-"}
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "2px 8px",
                      borderRadius: 10,
                      background: order.payment?.status === "paid" ? "#ecfdf5" : "#fff7ed",
                      color: order.payment?.status === "paid" ? "#10b981" : "#f59e0b",
                    }}>
                      {order.payment?.status || "-"}
                    </span>
                  </div>

                  {/* Status badge */}
                  <div style={{
                    fontSize: 12, fontWeight: 700, padding: "6px 14px", borderRadius: 20,
                    background: sStyle.bg, color: sStyle.text, border: `1px solid ${sStyle.border}`,
                    textAlign: "center", minWidth: 110,
                  }}>
                    {formatStatus(order.status)}
                  </div>

                  {/* Total */}
                  <div style={{ minWidth: 90, textAlign: "right", fontWeight: 800, fontSize: 16, color: "var(--mly-text)" }}>
                    ₹{order.totalAmount}
                  </div>

                  {/* Chevron */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="#bbb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOrders;